import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { matchesAll } from "@/lib/trFold";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Loader2, Search, Users } from "lucide-react";
import { toast } from "sonner";

/**
 * Duplicate order dialog. Lets user copy a sales order/quote to the same or
 * a different customer, previewing the target customer name before submit.
 */
export default function DuplicateOrderDialog({ order, open, onOpenChange, onDuplicated, currentCustomerId }) {
  const [mode, setMode] = useState("same"); // "same" | "other"
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode("same"); setSearch(""); setSelectedId("");
    api.get(`/customers`).then(({ data }) => setCustomers(data || []));
  }, [open]);

  const filtered = search.length >= 2
    ? customers.filter((c) => matchesAll(search, [c.name, c.phone, c.tax_no, c.contact_person])).slice(0, 20)
    : [];

  const submit = async () => {
    if (!order) return;
    setSaving(true);
    try {
      let url = `/orders/${order.id}/duplicate`;
      if (mode === "other") {
        if (!selectedId) { toast.error("Hedef müşteri seçin"); setSaving(false); return; }
        url += `?target_customer_id=${selectedId}`;
      }
      const { data } = await api.post(url);
      toast.success(`Kopyalandı: ${data.order_no} → ${data.customer_name}`);
      onOpenChange(false);
      onDuplicated?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="duplicate-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-blue-500" />
            Sipariş Kopyala · {order.order_no}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Kaynak: <b>{order.customer_name}</b> · <b>{order.product_name}</b> · {order.quantity} adet
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="dup-mode-same" onClick={() => setMode("same")}
              className={`p-3 rounded-sm border text-sm font-semibold ${mode === "same" ? "border-blue-500 bg-blue-500/10 text-blue-500" : "border-border bg-card text-muted-foreground"}`}>
              Aynı Müşteri
            </button>
            <button data-testid="dup-mode-other" onClick={() => setMode("other")}
              className={`p-3 rounded-sm border text-sm font-semibold ${mode === "other" ? "border-blue-500 bg-blue-500/10 text-blue-500" : "border-border bg-card text-muted-foreground"}`}>
              Farklı Müşteri
            </button>
          </div>
          {mode === "other" && (
            <div className="space-y-2">
              <Label className="text-xs uppercase font-mono text-muted-foreground">Müşteri Ara</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  data-testid="dup-customer-search"
                  placeholder="İsim, telefon veya vergi no ara (2+ harf)"
                  className="rounded-sm pl-8 h-10" />
              </div>
              {filtered.length > 0 && (
                <div className="border border-border rounded-sm max-h-56 overflow-y-auto"
                  data-testid="dup-customer-list">
                  {filtered.map((c) => (
                    <button key={c.id} onClick={() => setSelectedId(c.id)}
                      data-testid={`dup-customer-${c.id}`}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-muted border-b border-border last:border-b-0 ${selectedId === c.id ? "bg-blue-500/10" : ""}`}>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{c.phone || "—"} · {c.tax_no || "—"}</div>
                    </button>
                  ))}
                </div>
              )}
              {selectedId && (
                <div className="text-xs text-emerald-500 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  Hedef: <b>{customers.find((c) => c.id === selectedId)?.name}</b>
                </div>
              )}
            </div>
          )}
          <div className="text-[10px] text-muted-foreground border-t border-border pt-2">
            Kopya "teklif" aşamasında oluşacak. Ödemeler, revizyonlar ve tarihler sıfırlanır.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">İptal</Button>
          <Button onClick={submit} disabled={saving} data-testid="dup-confirm-btn"
            className="bg-blue-600 hover:bg-blue-500 rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Copy className="w-4 h-4 mr-1" />Kopyala</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
